/**
 * Shared contract between the web app, the Cloudflare Worker API and the engine.
 * Browser-safe: no node or bun imports. Change this file only by agreement; every side compiles against it.
 */

export type Source = "greenhouse" | "lever" | "ashby" | "workable" | "recruitee" | "bamboohr" | "jazzhr" | "workday" | "smartrecruiters";

/**
 * ATS sources the engine can fill and submit. The rest open on the company site: BambooHR and JazzHR put a visible
 * reCAPTCHA checkbox on every apply form, and the engine never solves captchas.
 */
export const FILLABLE: ReadonlySet<string> = new Set<Source>(["greenhouse", "lever", "ashby", "workable", "recruitee"]);

export const STATUSES = ["pending", "queued", "applying", "submitted", "confirmed", "failed", "skipped"] as const;
export type AppStatus = (typeof STATUSES)[number];

/** What came back from an application, as the user marks it. No outcome means no reply yet. `no_offer` is an interview that ended without one. */
export const OUTCOMES = ["rejected", "interview", "offer", "no_offer"] as const;
export type Outcome = (typeof OUTCOMES)[number];

export const DECISIONS = ["apply", "pass", "save"] as const;
export type Decision = (typeof DECISIONS)[number];

/** A job card, as `GET /api/feed`, `/api/jobs` and `/api/saved` return it. */
export interface Job {
  id: string;
  company: string;
  title: string;
  locations: string[];
  workplace: string;
  seniority: string | null;
  family: string | null;
  visa: string | null;
  ghost: number | null;
  salaryMin: number | null;
  salaryMax: number | null;
  salaryCurrency: string | null;
  salaryPeriod: string | null;
  salaryYearly: number | null;
  applyUrl: string | null;
  jobUrl: string;
  postedAt: string | null;
  hasForm: boolean;
  source: string;
  closed: boolean;
  canAutoApply: boolean;
  score?: number;
  descriptionSnippet?: string | null;
  /** The user's decision on this job, on `GET /api/jobs` items. Saved jobs stay listed; passed and applied ones drop out. */
  decision?: Decision | null;
}

export interface JobDetail extends Job {
  department: string | null;
  team: string | null;
  employmentType: string | null;
  descriptionText: string | null;
  decision: Decision | null;
  application: AppView | null;
}

/** An apply made on the company site (Workday, SmartRecruiters), which the engine never touches. */
export type AppViewStatus = AppStatus | "on_site";

export interface AppView {
  status: AppViewStatus;
  reason: string | null;
  reasonLabel: string;
  attempts: number;
  createdAt: string;
  updatedAt: string;
  submittedAt: string | null;
  outcome: Outcome | null;
  /** On a failed row, what it needs from the user: answers to `questions`, or finishing on the company site. */
  needs: Needs | null;
  /** On `GET /api/applications`, the required questions a failed row stopped on, each with the answer saved for it or "". */
  questions?: Question[];
}

export type Needs = "answers" | "site";

export interface Question {
  label: string;
  answer: string;
  /** The choices the form gave, when it asked with a short list or a checkbox. */
  options?: string[];
}

/** Most questions `POST /api/applications/:id/answers` takes at once, and the longest answer. */
export const ANSWERS_MAX = 20;
export const ANSWER_CHARS = 2000;

/**
 * `POST /api/applications/:id/answers` with `{ answers: Question[] }`, one per question the row stopped on. Saves each
 * to `profile.answers` for every form that asks it, then queues the application again.
 */
export interface AnswerResult {
  ok: true;
  application: AppView | null;
  profile: ProfileView;
}

export type Application = AppView & { job: Job | { id: string } };

export interface DecisionResult {
  decision: Decision;
  canAutoApply: boolean;
  manual: boolean;
  applyUrl: string | null;
  application: AppView | null;
  skipped: string | null;
  skippedLabel: string;
}

/** `GET /api/funnel`: the counts behind the Sankey chart on Applications. `sent` splits into `noReply`, `rejected` and `interviews`; `interviews` splits into `offers`, `noOffer` and the rest still in progress. */
export interface Funnel {
  /** Every role swiped right. Splits into `sent`, `waiting` and `failed`. */
  swiped: number;
  /** Submitted, confirmed, or applied on the company site. */
  sent: number;
  /** Pending, queued or applying. */
  waiting: number;
  /** Failed or skipped. */
  failed: number;
  noReply: number;
  rejected: number;
  interviews: number;
  offers: number;
  noOffer: number;
}

export interface Stats {
  appliedToday: number;
  appliedTotal: number;
  passes: number;
  saves: number;
  streakDays: number;
  applications: Record<AppStatus, number>;
  sentToday: number;
  dailyCap: number;
  dailyGoal: number;
  lastRefresh: string | null;
  refreshing: boolean;
}

export interface Education {
  school: string;
  degree?: string;
  field?: string;
  start?: string;
  end?: string;
  gpa?: string;
}

export interface Experience {
  company: string;
  title: string;
  start?: string;
  end?: string;
  summary?: string;
}

export type Seniority = "intern" | "new_grad" | "entry" | "mid" | "senior" | "staff_plus" | "manager";
export type Workplace = "remote" | "hybrid" | "onsite";

export interface Preferences {
  families: string[];
  titles: string[];
  excludeTitles: string[];
  seniority: Seniority[];
  locations: string[];
  workplace: Workplace[];
  minSalary?: number;
  excludeCompanies: string[];
  maxGhost: number;
  needsSponsorship: boolean;
}

/** The profile as stored in `profiles.data` (jsonb). Same shape as the engine's `src/apply/profile.ts` Profile, minus file paths. */
export interface Profile {
  firstName: string;
  lastName: string;
  preferredName?: string;
  email: string;
  phone: string;
  pronouns?: string;
  address?: { street?: string; city?: string; state?: string; postal?: string; country?: string };
  links: { linkedin?: string; github?: string; website?: string; portfolio?: string; twitter?: string };
  currentCompany?: string;
  currentTitle?: string;
  yearsExperience?: number;
  education: Education[];
  experience: Experience[];
  skills: string[];
  story?: string;
  workAuth: { authorizedToWork: boolean; needsSponsorship: boolean; citizenship?: string; securityClearance?: string; exportControlUsPerson?: boolean };
  relocation: { willing: boolean; places: string[] };
  attestations: { convicted?: boolean; formerEmployee?: boolean };
  startDate?: string;
  salaryExpectation?: string;
  howHeard: string;
  eeo: { gender?: string; race?: string; veteran?: string; disability?: string; hispanic?: string };
  prefs: Preferences;
  answers: Record<string, string>;
  dailyGoal?: number;
  onboardingStep?: number;
  /** The resume's file name on the user's computer; storage keeps every upload as `resume.<ext>`. */
  resumeFile?: string;
}

/** The profile as the browser sees it: the resume's storage path replaced by its name. */
export type ProfileView = Profile & { resumeName: string | null; hasResume: boolean };

/**
 * Resume files the app takes, by extension, with the type each is stored as. The bucket's `allowed_mime_types` and the
 * `{uid}/resume.{ext}` checks in the database, storage policies and API all hold this same list.
 */
export const RESUME_TYPES = {
  pdf: "application/pdf",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  doc: "application/msword",
  rtf: "application/rtf",
  odt: "application/vnd.oasis.opendocument.text",
  txt: "text/plain",
  md: "text/markdown",
} as const;

export type ResumeExt = keyof typeof RESUME_TYPES;

/** The file picker's `accept`: every extension in `RESUME_TYPES`. */
export const RESUME_ACCEPT = Object.keys(RESUME_TYPES)
  .map((e) => `.${e}`)
  .join(",");

/** The resume's type from its file name, or null when the app doesn't take it. */
export function resumeExt(name: string): ResumeExt | null {
  const ext = name.match(/\.([a-z0-9]+)$/i)?.[1]?.toLowerCase() ?? "";
  return Object.hasOwn(RESUME_TYPES, ext) ? (ext as ResumeExt) : null;
}

export interface Onboarding {
  /** 0 to 3: steps finished. */
  step: number;
  done: boolean;
  /** What stops the engine from sending anything: e.g. "resume", "phone". */
  missing: string[];
}

export interface EngineEvent {
  at: string;
  jobId: string;
  kind: "queued" | "applying" | "submitted" | "confirmed" | "failed" | "skipped";
  status: AppStatus;
  reason: string | null;
  reasonLabel: string;
  needs: Needs | null;
  company?: string;
  title?: string;
}

/** `GET /api/jobs/:id/fit`: the model's one-paragraph read on why this role fits, from Muse Wonder. */
export interface Fit {
  summary: string;
  pros: string[];
  cons: string[];
  /** 0 to 100. */
  score: number;
}

/** Search params of `GET /api/jobs`. All optional. */
export interface SearchParams {
  q?: string;
  family?: string[];
  seniority?: string[];
  workplace?: string[];
  location?: string;
  source?: string[];
  minSalary?: number;
  postedWithinDays?: number;
  autoApply?: boolean;
  cursor?: string;
  limit?: number;
}

export type Plan = "free" | "autopilot" | "max";

/** What each plan costs and allows. The landing page sells these numbers; `daily` keeps the monthly allowance reachable without a burst. */
export const PLANS: Record<Plan, { name: string; price: number; monthly: number; daily: number }> = {
  free: { name: "Free", price: 0, monthly: 10, daily: 10 },
  autopilot: { name: "Autopilot", price: 39, monthly: 1000, daily: 100 },
  max: { name: "Max", price: 69, monthly: 2500, daily: 250 },
};

/**
 * Extra Free applications for a job alert subscriber who creates an account by the deadline in their sign-up offer email,
 * in the UTC calendar month the account is created.
 */
export const OFFER_BONUS = 10;

/** A signup ref, from `?ref=` or `?utm_source=` on a link: lower case letters, digits, `-` and `_`, as 0025 checks. */
export const REF_PATTERN = /^[a-z0-9_-]{1,40}$/;

/** `GET /api/billing`. Applications count once queued; failed and skipped ones never count. */
export interface Billing {
  plan: Plan;
  /** Stripe subscription status, null on free. */
  status: string | null;
  used: number;
  /** `PLANS[plan].monthly`, plus `OFFER_BONUS` on Free in the month a job alert offer was taken up. */
  limit: number;
  periodStart: string;
  /** Free resets at the start of the next UTC month. */
  periodEnd: string;
  cancelAtPeriodEnd: boolean;
  /** Whether the billing portal can open. */
  hasCustomer: boolean;
}

/** Where an intern application stands. Only the founder moves it forward; the applicant can only withdraw it. */
export const CAREER_STATUSES = ["submitted", "reviewing", "interviewing", "offer", "closed", "withdrawn"] as const;
export type CareerStatus = (typeof CAREER_STATUSES)[number];

/** Statuses an applicant can still withdraw from. */
export const WITHDRAWABLE: readonly CareerStatus[] = ["submitted", "reviewing", "interviewing", "offer"];

/**
 * An open role on /careers, written in `shared/careers.ts`. The short fields are plain text shown as written in the
 * posting's header, and each list is a section; an empty field or list renders nothing.
 */
export interface Role {
  slug: string;
  title: string;
  /** One sentence for the role list and the top of the posting. */
  summary: string;
  /** Such as "Remote (US)". */
  location: string;
  /** Such as "Intern". */
  employmentType: string;
  /** Such as "Engineering". */
  department: string;
  /** Such as "$45/hr". */
  pay: string;
  /** Such as "40 hours/week". */
  hours: string;
  /** Such as "June to August 2027". */
  dates: string;
  /** The day the posting went up, as YYYY-MM-DD. Google for Jobs shows it as the posting date. */
  posted: string;
  /** The pay range in numbers, for the JobPosting data search engines read. `pay` is what the page shows. */
  salary: { min: number; max: number; per: "HOUR" | "YEAR" };
  aboutCompany: string[];
  aboutRole: string[];
  work: string[];
  lookingFor: string[];
  niceToHave: string[];
  compensation: string[];
  /** Whether it takes applications. A closed role still shows to anyone who applied. */
  open: boolean;
}

/**
 * What an applicant fills in, as `POST` and `PUT /api/careers/applications/:role` take it. Optional links are "" when left
 * out. Signed in, the email comes from the account and the resume sits at `resumes/{user id}/careers/{role}.pdf`, so
 * neither is sent. Signed out, `POST /api/careers/guest/:role` takes these fields with the email and the resume file in
 * one multipart form, and the resume goes to `resumes/careers/{application id}.pdf`.
 */
export interface CareerForm {
  fullName: string;
  phone: string;
  linkedin: string;
  github: string;
  website: string;
  school: string;
  /** YYYY-MM. */
  graduation: string;
  /** The role needs US work authorization, so only true is accepted. */
  authorizedUs: boolean;
  /** YYYY-MM-DD. */
  startDate: string;
  /** A project the applicant built. Optional; empty when skipped. */
  project: string;
  /** The resume's file name on the applicant's computer. */
  resumeName: string;
  /** Whether the applicant wants to hear about future roles. Only recorded; nothing sends mail from it yet. */
  updatesOptIn: boolean;
}

/** `GET /api/careers/applications` items: the signed-in user's own applications. */
export interface CareerApplication extends CareerForm {
  role: string;
  email: string;
  status: CareerStatus;
  createdAt: string;
  updatedAt: string;
}

/**
 * The emails a user can turn off: the one-time reminder to finish setting up, the hourly note when an application stops
 * on questions, the note when Free's monthly applications run out, and new matches while they are away.
 */
export const EMAIL_KINDS = ["setup_reminder", "application_updates", "limit_reached", "new_matches"] as const;
export type EmailKind = (typeof EMAIL_KINDS)[number];

/** `GET`/`PUT /api/email-prefs`: one switch per kind, all on by default. */
export type EmailPrefs = Record<EmailKind, boolean>;

/** The emails a job alert subscriber gets. One unsubscribe stops all of them. */
export const ALERT_KINDS = ["alert_welcome", "alert_founder", "alert_weekly", "alert_offer", "alert_lapse"] as const;
export type AlertKind = (typeof ALERT_KINDS)[number];

/** `GET /api/public/jobs` items: an open job as anyone can see it, signed in or not. */
export interface PublicJob {
  id: string;
  company: string;
  title: string;
  locations: string[];
  workplace: string | null;
  postedAt: string | null;
  jobUrl: string;
  applyUrl: string | null;
  canAutoApply: boolean;
}

/** What a job alert searches for: `q` is 2 to 80 characters, `location` at most 80 or null for anywhere. */
export interface AlertSearch {
  q: string;
  location: string | null;
}

/** `POST /api/alerts` body; answer is `{ ok: true }` (201). */
export interface AlertSubscribe extends AlertSearch {
  email: string;
}

/** `GET /api/alerts/edit?t=` ; PUT same path with AlertSearch body returns the same shape. */
export interface AlertView extends AlertSearch {
  email: string;
  active: boolean;
}

/** `GET /api/unsubscribe?t=`: what a signed unsubscribe link turns off, `"job_alerts"` for a subscriber link, or null when the link is bad. */
export interface UnsubscribeInfo {
  kind: EmailKind | "job_alerts" | null;
}
